import { Auth } from 'aws-amplify';
import { basePost, baseGet, useRootStore } from '@qsrsoft/myq-core';

const baseURL = import.meta.env.VITE_APP_SECURITY_API_BASE_URL;

async function getToken() {
  const currentSession = await Auth.currentSession();
  const { idToken } = currentSession;
  return idToken.jwtToken;
}

export async function getAPIParms(url, params) {
  const token = await getToken();
  const response = await fetch(
    `${resource}/${url}`,
    { params },
    {
      headers: {
        'x-auth-token': token,
      },
    },
  );
  return response.json();
}

export function postConfigAPI(name, settingName, array) {
  const route = `/user/settings?appName=security&settingName=${name}/${settingName}`;
  return basePost(route, array);
}

export async function getConfigAPI(name, settingName) {
  const route = name
    ? `/user/settings?appName=security&settingName=${name}/${settingName}`
    : '/user/settings?appName=security';
  try {
    const data = await baseGet(route);
    return settingName ? data[settingName] : data;
  } catch (ex) {
    console.error('getConfigAPI error route', route, ex);
  }
}

export function deleteConfigAPI(name, settingName) {
  const route = `/user/settings?appName=security&settingName=${name}/${settingName}`;
  return basePost(route, null);
}

export async function get(url, params = {}) {
  const orgId = useRootStore().orgId;
  const token = await getToken();
  const urlParams = new URLSearchParams({ ...params, orgId });
  const urlStr = `${baseURL}/${url}?${urlParams.toString()}`;
  const response = await fetch(urlStr, {
    headers: {
      'x-auth-token': token,
    },
  });
  return response.json();
}

/**
 * Constructs an HTTP GET request and returns the executable request promise as well
 * as a cancel function.
 *
 * @param {string} url the URL of the API resource to query
 * @param {Object} params the query parameters
 *
 * @returns {Object} an object containing the `request` promise and it's associated `cancel` function
 */
export async function getAPIPending(url, params = {}) {
  const orgId = useRootStore().orgId;
  const controller = new AbortController();
  const signal = controller.signal;
  const urlParams = new URLSearchParams({ ...params, orgId });
  const urlStr = `${baseURL}/${url}?${urlParams.toString()}`;
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const request = fetch(urlStr, {
    signal,
    headers: {
      'x-auth-token': await getToken(),
    },
  })
    .then((resp) => {
      console.log(
        `[securityService.getAPIPending] Request ${requestId} completed successfully for: ${url}`,
      );
      return resp.json();
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        console.log(
          `[securityService.getAPIPending] Request ${requestId} was CANCELLED (AbortError) for: ${url}`,
        );
        error.__CANCEL__ = true;
      }
      throw error;
    });

  const cancel = () => {
    console.log(
      `[securityService.getAPIPending] Cancel function called for request ${requestId} - aborting: ${url}`,
    );
    controller.abort();
  };

  return { request, cancel };
}

const BATCH_DAYS = 3;

/**
 * Parse YYYY-MM-DD as local date (no UTC shift). new Date("YYYY-MM-DD") is UTC midnight
 * and getFullYear/getMonth/getDate in local time can shift the calendar day.
 */
function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (y == null || m == null || d == null) return new Date(NaN);
  return new Date(y, m - 1, d);
}

/**
 * Splits a date range into consecutive batches of up to BATCH_DAYS days each.
 * Batches overlap by one day at boundaries so the API's end_date (if exclusive)
 * or store_busn_dt vs event_dt semantics don't drop boundary events.
 *
 * @param {string} startDateStr - YYYY-MM-DD
 * @param {string} endDateStr - YYYY-MM-DD
 * @returns {Array<{ start_date: string, end_date: string }>}
 */
export function getDateBatches(startDateStr, endDateStr) {
  const start = parseLocalDate(startDateStr);
  const end = parseLocalDate(endDateStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
    return [{ start_date: startDateStr, end_date: endDateStr }];
  }
  const [y0, m0, d0] = startDateStr.split('-').map(Number);
  const [y1, m1, d1] = endDateStr.split('-').map(Number);
  let chunkStartUtc = Date.UTC(y0, m0 - 1, d0);
  const endUtc = Date.UTC(y1, m1 - 1, d1);
  const dayMs = 24 * 60 * 60 * 1000;
  const batches = [];
  let current = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (current <= endLocal) {
    const batchEnd = new Date(current);
    batchEnd.setDate(batchEnd.getDate() + BATCH_DAYS - 1);
    const rangeEnd = batchEnd <= endLocal ? batchEnd : endLocal;
    batches.push({
      start_date: formatDate(current),
      end_date: formatDate(rangeEnd),
    });
    // Next batch starts on this batch's end_date (1-day overlap) to avoid boundary gaps
    current = new Date(
      rangeEnd.getFullYear(),
      rangeEnd.getMonth(),
      rangeEnd.getDate(),
    );
    if (current.getTime() >= endLocal.getTime()) break;
  }
  return batches;
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Deduplicate event_details rows by order_key (keep first = chronological order). */
function dedupeByOrderKey(rows) {
  if (!Array.isArray(rows) || !rows.length) return rows;
  const seen = new Set();
  return rows.filter((row) => {
    const key =
      row?.order_key ?? `${row?.event_dt}_${row?.event_tm}_${row?.reg_num}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * POST to event_details API with the date range split into batches of 3 days (UTC).
 * Matches production src/helpers/securityService.js: same date ranges, dedupe by order_key,
 * and sort by event_dt/event_tm desc so batched results match the single production API call.
 *
 * @param {string} url - e.g. security/event_details/v1/{orgId}/{nsn}
 * @param {Object} payload - request body; must include start_date and end_date (YYYY-MM-DD)
 * @param {Object} params - optional query params (e.g. { orgId })
 * @returns {Promise<Array>} merged, deduped array sorted by event_dt then event_tm (newest first)
 */
export async function postEventDetailsBatched(url, payload, params = {}) {
  const startStr = String(payload?.start_date ?? '').slice(0, 10);
  const endStr = String(payload?.end_date ?? '').slice(0, 10);
  if (!startStr || !endStr) {
    const result = await post(url, payload, params);
    return Array.isArray(result) ? result : [result];
  }
  const batches = getDateBatches(startStr, endStr);
  if (batches.length === 0) {
    const result = await post(url, payload, params);
    return Array.isArray(result) ? result : [result];
  }
  if (batches.length === 1) {
    const result = await post(
      url,
      { ...payload, start_date: startStr, end_date: endStr },
      params,
    );
    return Array.isArray(result) ? result : [result];
  }
  const debug =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('DEBUG_EVENT_DETAILS_BATCH') === 'true';

  const results = await Promise.all(
    batches.map(({ start_date: sd, end_date: ed }) =>
      post(url, { ...payload, start_date: sd, end_date: ed }, params),
    ),
  );
  const invalid = results.find((r) => !Array.isArray(r));
  if (invalid !== undefined) {
    throw new Error('event_details batch returned non-array', {
      cause: invalid,
    });
  }
  const combined = results.flat();
  const deduped = dedupeByOrderKey(combined);
  const sortKey = (row) => {
    const d = (row.event_dt || row.store_busn_dt || '').slice(0, 10);
    const t = row.event_tm || row.useTime || '';
    return `${d} ${t}`;
  };
  deduped.sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    return ka > kb ? -1 : ka < kb ? 1 : 0;
  });
  return deduped;
}

export async function post(url, payload, params = {}) {
  if (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('DEBUG_ERROR_MESSAGES') === 'true'
  ) {
    throw new Error('Simulated API error');
  }
  const orgId = useRootStore().orgId;
  const token = await getToken();
  const urlParams = new URLSearchParams({ ...params, orgId });
  const urlStr = `${baseURL}/${url}?${urlParams.toString()}`;
  const response = await fetch(urlStr, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify(payload),
  });
  return response.json();
}

/**
 * Constructs an HTTP POST request and returns the executable request promise as well
 * as a cancel function.
 *
 * @param {string} url the URL of the API resource to query
 * @param {Object} payload the request body
 * @param {Object} params the query parameters
 *
 * @returns {Object} an object containing the `request` promise and it's associated `cancel` function
 */
export async function postAPIPending(url, payload, params = {}) {
  if (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('DEBUG_ERROR_MESSAGES') === 'true'
  ) {
    return {
      request: Promise.reject(new Error('Simulated API error')),
      cancel: () => {},
    };
  }
  const orgId = useRootStore().orgId;
  const controller = new AbortController();
  const signal = controller.signal;
  const urlParams = new URLSearchParams({ ...params, orgId });
  const urlStr = `${baseURL}/${url}?${urlParams.toString()}`;
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const requestPromise = fetch(urlStr, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': await getToken(),
    },
    body: JSON.stringify(payload),
  })
    .then((resp) => {
      console.log(
        `[securityService.postAPIPending] Request ${requestId} completed successfully for: ${url}`,
      );
      return resp.json();
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        console.log(
          `[securityService.postAPIPending] Request ${requestId} was CANCELLED (AbortError) for: ${url}`,
        );
        error.__CANCEL__ = true;
      }
      throw error;
    });

  const cancel = () => {
    console.log(
      `[securityService.postAPIPending] Cancel function called for request ${requestId} - aborting: ${url}`,
    );
    controller.abort();
  };
  return { request: requestPromise, cancel };
}

export default {
  get,
  post,
  postEventDetailsBatched,
  getAPIPending,
  postAPIPending,
};
